// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The simulation world and its fixed-step loop.
//
// Everything here runs at exactly 1000 Hz regardless of what the renderer is doing. The
// renderer interpolates between states; it never advances the simulation. That separation
// is why a phone dropping to 30 fps plays the same game as a Quest at 90 Hz — the player
// may see a less pretty machine, never a different one.
//
// A step runs in four ordered phases, and the order is part of the contract (PH-084):
//
//   1. **Machinery** — the frame settles, flippers swing, devices run their own dynamics.
//   2. **Integration** — forces accumulate, every ball moves, the swept guard clamps any
//      ball that would have jumped a collider.
//   3. **Contacts** — iterated to convergence over surfaces, statics, flippers, device
//      contacts and ball pairs, with every ball inside the same iteration so a pile settles
//      together rather than one ball at a time.
//   4. **Settling** — rolling resistance, clamps, orientation, then region observation.
//
// Within each phase, iteration is by array index only: balls by id, devices by index,
// colliders by index. No hash-map enumeration appears anywhere, which is what makes a
// replay reproducible on a different runtime.
//
// See 02 §5 and §10.

import { Ball } from './Ball'
import { Broadphase } from './Broadphase'
import { Flipper } from './Flipper'
import { Rng } from '../rng/Rng'
// The core's own transcendentals. `Math.sin` is not bit-identical across V8, .NET and IL2CPP, and
// `core-purity.mjs` refuses it — which is what makes a re-simulated leaderboard score worth having.
import { clamp, cos, sin } from '../math/deterministic'
import {
  applyContactImpulse,
  applyRollingResistance,
  correctPenetration,
  SURFACE_BALL,
  type ContactResult,
  type ContactTap,
} from './contact'
import type { Collider, Contact } from './colliders'
import { colliderContact, createContact, taperedCapsuleContact } from './colliders'
import type { Material, MaterialName } from './materials'
import { applyOverrides, effectiveRestitution, type MaterialOverride } from './materials'
import type { Device } from './devices/Device'
import type { TriggerRegion } from './devices/Trigger'
import { EventQueue } from './events'
import { NO_HIT, sweptColliderTOI } from './swept'
import {
  A_DOWNFIELD,
  A_NORMAL,
  BALL_DIAMETER,
  BALL_RADIUS,
  CONTACT_REPORT_SPEED,
  GLASS_HEIGHT,
  MIN_SEPARATION_VELOCITY,
  PENETRATION_SLOP,
  REST_SPEED,
  FLIPPER_CONTACT_TILT,
  MAX_FLIPPER_CONTACT_TILT,
  SPEED_CLAMP,
  STEP_DT,
} from './constants'

/** Passes over the contact set per step. Enough to settle a ball wedged between two walls. */
const CONTACT_ITERATIONS = 4

/** Rolling resistance coefficient, per second. */
const ROLLING_RESISTANCE = 0.6

/** Machine mass, kg. A real cabinet is 200–300 lb; nudge impulses divide by this. */
const MACHINE_MASS = 120

/** Nudge frame spring and damping, tuned so a shove settles in about a third of a second. */
const FRAME_STIFFNESS = 900
const FRAME_DAMPING = 42

/** Largest frame displacement a nudge can produce, metres. */
const FRAME_MAX_DISPLACEMENT = 0.012

/**
 * Travel per step, as a fraction of the ball radius, above which the swept guard runs.
 *
 * Below it the discrete test cannot miss anything: the ball's own footprint overlaps its
 * previous one by a wide margin. The guard therefore costs nothing at all for a ball
 * rolling around the playfield and only engages above roughly 4 m/s, which is where the
 * risk actually lives.
 */
const SWEPT_TRAVEL_FRACTION = 0.3

/** How far past first contact the guard places the ball, so the discrete solver sees it. */
const SWEPT_BIAS = 2e-5

export interface WorldOptions {
  readonly colliders?: readonly Collider[]
  readonly flippers?: readonly Flipper[]
  readonly devices?: readonly Device[]
  readonly ballCount?: number
  readonly seed?: number
  readonly materialOverrides?: Partial<Record<MaterialName, MaterialOverride>>
  /** Set false for a flat test rig with no downfield gravity. */
  readonly gravity?: boolean
  /**
   * Underside of the glass, metres above the playfield.
   *
   * Per-machine, because it is set by the tallest ramp the machine has to fit underneath.
   * A machine whose glass is lower than its own geometry traps every ball that climbs.
   */
  readonly glassHeight?: number
  /**
   * Hard speed clamp, m/s. Defaults to `SPEED_CLAMP`.
   *
   * Per-machine (`physics.ballSpeedClamp`, 04 §10) because it is a property of the machine's
   * strongest mechanism, not of the simulator: a table with a 6 m/s auto-plunger and a table
   * with a cannon need different headroom above the swept narrow phase.
   */
  readonly speedClamp?: number
  /**
   * Plane layers that occupy only part of the playfield (04 §5's `layers[].bounds`, M9-45).
   *
   * In declaration order, and the **first** one containing a ball is the layer that ball is on;
   * a ball inside none of them is on `homePlane`. Order rather than area or nesting, because
   * "first match wins, reading top to bottom" is the rule the rest of the format already uses for
   * rules and for lamps, and an area comparison would make the answer depend on floating-point
   * arithmetic over the author's numbers.
   *
   * Empty for every machine that declares no bounded plane — which is both shipped machines, and
   * is why this changes nothing they do.
   */
  readonly boundedPlanes?: readonly BoundedPlane[]
  /**
   * The layer a ball is on when it is inside no bounded plane. Defaults to 0, the playfield.
   *
   * `load.ts` numbers layers in declaration order and 04 §5 puts the playfield first, so this is
   * only ever not 0 for a machine that declares a bounded plane above its unbounded one.
   */
  readonly homePlane?: number
}

/** One entry of `WorldOptions.boundedPlanes`. */
export interface BoundedPlane {
  /** Layer index, as `load.ts` numbers them. */
  readonly layer: number
  readonly region: TriggerRegion
  readonly minEntryZ?: number
  readonly floorZ?: number
}

export class World {
  readonly balls: Ball[] = []
  readonly flippers: Flipper[]
  readonly devices: readonly Device[]
  readonly colliders: readonly Collider[]
  readonly materials: Record<MaterialName, Material>
  readonly rng: Rng
  readonly events = new EventQueue()

  /**
   * Installed by the presentation layer, absent everywhere else (M3-24).
   *
   * Not an event queue: see `ContactTap`. The solver knows the impact speed and the material of
   * every contact it resolves and, until this existed, threw both away — so the ball layer had
   * nothing to be loud about and nothing to know what it hit. Read-only, unqueued, and
   * `undefined` outside a browser.
   */
  contactTap: ContactTap | undefined = undefined
  /** Underside of the glass, metres above the playfield. */
  readonly glassHeight: number
  /** Hard speed clamp, m/s — the machine's override of `SPEED_CLAMP`. */
  readonly speedClamp: number

  /**
   * Plane layers occupying only part of the playfield (04 §5, M9-45), in declaration order.
   *
   * **Empty for every machine that does not declare one**, and the whole feature is then a single
   * `length === 0` test per step — which is what keeps a format feature nobody uses off the hot
   * path, and what makes this land without moving a single recorded game.
   */
  private readonly heightAwarePlanes: boolean
  readonly boundedPlanes: readonly BoundedPlane[]

  /** The plane a ball is on when it is inside no bounded one. Index 0 by convention. */
  readonly homePlane: number

  /**
   * Every layer index that is a plane, bounded or not.
   *
   * The set this re-derivation is allowed to touch. A ball on a ramp, a wire or in the trough has
   * been *put* on that layer by the mechanism that owns it, and a position test must not take it
   * off again — a habitrail passing over the shooter lane is the obvious way to lose a ball.
   */
  private readonly planeLayers: Set<number>

  /** Monotonic step counter. The simulation's only notion of time (PH-086). */
  stepIndex = 0

  /**
   * How far a flipper's contact normal tilts out of the playfield plane, radians (G7-01).
   *
   * **Zero in every shipped configuration, every fixture and every game**, which is what makes it
   * safe to have: `Math.cos(0)` and `Math.sin(0)` are exactly 1 and 0 in binary64, so a world at
   * zero produces the same bits it produced before this field existed.
   *
   * A field rather than a constructor option, because the only thing that ever sets it is a
   * measurement harness setting it on a world it has just built — and an option would put it in the
   * machine definition's reach, which is precisely where a physics *decision* must not be.
   *
   * See `FLIPPER_CONTACT_TILT` for the question it exists to answer.
   */
  flipperContactTilt = FLIPPER_CONTACT_TILT

  /** Machine-frame displacement and velocity, from nudging (PH-070). */
  frameX = 0
  frameY = 0
  frameVX = 0
  frameVY = 0

  /**
   * The last shove: which step, and how hard (M3-26).
   *
   * The same arrangement as `Flipper.pressedAtStep`, and for the same reason. A nudge is not a
   * physical event the solver produces — it is something done *to* the machine from outside —
   * so there is no event to carry it and nothing downstream could tell a shove from the frame
   * spring settling afterwards. A layer polling `frameVX` once a frame sees a velocity without
   * knowing whether it is a fresh shove or the tail of the last one, and a cabinet that thuds
   * once per frame while the frame rings is worse than one that never thuds at all.
   *
   * Written and never read by the simulation. Costs two assignments per nudge, which happens a
   * few times a ball.
   */
  nudgedAtStep = -1
  nudgeImpulseX = 0
  nudgeImpulseY = 0

  private readonly broadphase = new Broadphase()
  private readonly contact: Contact = createContact()
  private readonly surfaceVelocity = { x: 0, y: 0 }
  /**
   * Scratch for the one collider that pushes back — reused, never allocated per contact.
   *
   * Mutable where `ReactingBody` is readonly, which is deliberate: the solver must not be able
   * to keep it, and the world must be able to refill it a thousand times a second without
   * putting an object into the young generation on every flipper touch.
   */
  private readonly reactingBody = { invInertia: 0, rx: 0, ry: 0 }
  private readonly gravityOn: boolean

  // Devices split by the phase they participate in, so the common case of a device that
  // only owns geometry costs nothing per ball per step.
  private readonly forceDevices: readonly Device[]
  private readonly contactDevices: readonly Device[]
  private readonly observeDevices: readonly Device[]

  /**
   * Times the swept guard clamped a ball back to first contact.
   *
   * Deliberately *not* named "tunnels prevented": the guard engages whenever a fast ball's
   * path crosses a collider, which is usually a contact the discrete test would also have
   * found. It is a diagnostic for how often the fast path is exercised, not a count of
   * disasters averted.
   */
  sweptClamps = 0

  constructor(options: WorldOptions = {}) {
    this.devices = [...(options.devices ?? [])]
    // Index first, geometry second: a device stamps its own index into every collider it
    // contributes, and that stamp is how a hit finds its way back to the mechanism.
    for (let i = 0; i < this.devices.length; i++) this.devices[i]!.index = i

    const colliders: Collider[] = [...(options.colliders ?? [])]
    for (const device of this.devices) colliders.push(...device.colliders())
    this.colliders = colliders

    this.forceDevices = this.devices.filter((d) => d.wantsForce)
    this.contactDevices = this.devices.filter((d) => d.wantsContact)
    this.observeDevices = this.devices.filter((d) => d.wantsObserve)

    this.flippers = [...(options.flippers ?? [])]
    this.materials = applyOverrides(options.materialOverrides)
    this.rng = new Rng(options.seed ?? 1)
    this.gravityOn = options.gravity ?? true
    this.glassHeight = options.glassHeight ?? GLASS_HEIGHT
    this.speedClamp = options.speedClamp ?? SPEED_CLAMP
    this.boundedPlanes = [...(options.boundedPlanes ?? [])]
    this.heightAwarePlanes = this.boundedPlanes.some(p => p.minEntryZ !== undefined)
    this.homePlane = options.homePlane ?? 0
    this.planeLayers = new Set<number>([this.homePlane])
    for (const plane of this.boundedPlanes) this.planeLayers.add(plane.layer)
    if (this.glassHeight <= BALL_DIAMETER) {
      throw new Error(
        `World: glass at ${this.glassHeight} m is lower than the ball's own ${BALL_DIAMETER} m diameter`,
      )
    }

    const count = options.ballCount ?? 1
    for (let i = 0; i < count; i++) this.balls.push(new Ball(i))

    this.broadphase.build(this.colliders, BALL_DIAMETER)
  }

  /** Put a ball into play at a playfield position, resting on the surface. */
  spawnBall(index: number, x: number, y: number): Ball {
    const ball = this.balls[index]
    if (!ball) throw new RangeError(`spawnBall: no ball at index ${index}`)
    ball.reset(x, y, BALL_RADIUS)
    ball.active = true
    return ball
  }

  /** Balls currently in play. */
  activeBalls(): Ball[] {
    return this.balls.filter((b) => b.active)
  }

  /** Return every device to its ball-start state and discard pending events. */
  resetDevices(): void {
    for (const d of this.devices) d.reset()
    this.events.clear()
  }

  /** Look a device up by its author-facing name. Not for the hot path. */
  device(id: string): Device | undefined {
    return this.devices.find((d) => d.id === id)
  }

  /**
   * Shove the machine.
   *
   * The impulse goes into the machine *frame*, never into a ball. A ball touching nothing
   * is therefore unaffected, and a ball resting against a rail gets slapped hard — which
   * is what nudging physically is, and why PH-A26 is a meaningful test.
   */
  nudge(impulseX: number, impulseY: number): void {
    this.frameVX += impulseX / MACHINE_MASS
    this.frameVY += impulseY / MACHINE_MASS
    // Two shoves inside the same millisecond are one shove of their sum, not the second one.
    // A frame of input can queue several — a player leaning on two direction keys — and the
    // physics already adds them, so the stamp has to as well or the cabinet answers the last
    // one at its own strength instead of the pair at theirs.
    if (this.nudgedAtStep !== this.stepIndex) {
      this.nudgedAtStep = this.stepIndex
      this.nudgeImpulseX = 0
      this.nudgeImpulseY = 0
    }
    this.nudgeImpulseX += impulseX
    this.nudgeImpulseY += impulseY
  }

  /**
   * Turning wall-clock time into steps is **not** the world's job, and there is no method here
   * that does it.
   *
   * There used to be: an `advance(elapsedSeconds)` with its own accumulator, its own 40-step
   * cap, its own lost-step count and an `interpolationAlpha()` to read the leftover. A second
   * fixed-step driver, inside the core, with different drop behaviour from the real one — it
   * dropped inside the loop and broke out, where `SimulationDriver` runs the cap and then
   * declares the remainder, and it had no maximum-frame clamp at all, so a tab returning from
   * the background would have handed it fifteen minutes.
   *
   * Two policies for the one operation the whole determinism story rests on. A leaderboard
   * score is validated by re-simulating the player's input trace on a server with no display,
   * so if the client's seconds-to-steps arithmetic is not the server's, the score does not
   * validate — and the two would have diverged the first time anything called this.
   *
   * So the world exposes `step()`, and `src/engine/SimulationDriver.ts` is the single place
   * that owns the conversion (PH-022, PH-023, ARCH-012). A replay validator calls `step()` N
   * times and never sees a duration at all, which is exactly what the "no clock in the core"
   * rule is protecting.
   */

  /** One 1000 Hz step. */
  step(): void {
    const dt = STEP_DT
    this.stepIndex++

    // --- 1. machinery ------------------------------------------------------
    this.stepFrame(dt)
    for (const f of this.flippers) f.step(dt)
    for (const d of this.devices) if (d.enabled) d.step(this, dt)

    // --- 2. integration ----------------------------------------------------
    for (const ball of this.balls) {
      // Recorded even for a held ball, so a scoop eject does not draw as a teleport.
      ball.prevX = ball.x
      ball.prevY = ball.y
      ball.prevZ = ball.z
      if (!this.simulated(ball)) continue
      this.integrate(ball, dt)
    }

    // --- 2a. which plane each ball is on (04 §5's `bounds`, M9-45) ----------
    // After integration and before contacts, so a ball that has just rolled into a bounded plane
    // is filtered against *that* plane's colliders on the step it arrives rather than the step
    // after — a one-step lag here is a ball passing through the lane divider at 6 m/s.
    if (this.boundedPlanes.length > 0) {
      for (const ball of this.balls) {
        if (!this.simulated(ball)) continue
        if (!this.planeLayers.has(ball.layer)) continue
        ball.layer = this.planeAt(ball.x, ball.y, ball.z)
        if (this.heightAwarePlanes) {
          ball.floorZ = 0
          for (const plane of this.boundedPlanes) if (plane.layer === ball.layer) { ball.floorZ = plane.floorZ ?? 0; break }
        }
      }
    }

    // --- 3. contacts -------------------------------------------------------
    for (let pass = 0; pass < CONTACT_ITERATIONS; pass++) {
      let any = false
      for (const ball of this.balls) {
        if (!this.simulated(ball)) continue
        if (this.resolveSurfaces(ball)) {
          any = true
          ball.touchingSurface = true
        }
        if (this.resolveStatics(ball)) any = true
        if (this.resolveFlippers(ball)) any = true
        for (const d of this.contactDevices) {
          if (d.enabled && (d.contactLayer < 0 || d.contactLayer === ball.layer) && d.resolveContact(this, ball)) any = true
        }
      }
      if (this.resolveBallPairs()) any = true
      if (!any) break
    }

    // --- 4. settling -------------------------------------------------------
    for (const ball of this.balls) {
      if (!this.simulated(ball)) continue
      if (ball.touchingSurface) applyRollingResistance(ball, ROLLING_RESISTANCE, dt)
      ball.clampSpeed(this.speedClamp)
      ball.integrateOrientation(dt)
      ball.stillSteps = ball.speed() < REST_SPEED ? ball.stillSteps + 1 : 0
      for (const d of this.observeDevices) if (d.enabled && (d.contactLayer < 0 || d.contactLayer === ball.layer)) d.observe(this, ball)
    }
  }

  /**
   * The plane layer that owns a point (04 §5, M9-45).
   *
   * Public because it is also the honest answer to "where does a ball *start*": a trough serving
   * into a shooter lane has to put the ball on the lane's layer, and asking this is better than
   * every caller repeating the containment test.
   *
   * **Declaration order, first match wins.** No area comparison, no nesting rule: those would make
   * the answer depend on floating-point arithmetic over an author's numbers, and this is a layer
   * assignment a leaderboard score is validated against.
   *
   * Only `+ − × ÷` and comparisons, so both cores reach the same layer for the same point.
   */
  planeAt(x: number, y: number, z = Infinity): number {
    for (const plane of this.boundedPlanes) {
      if (plane.minEntryZ !== undefined && z < plane.minEntryZ) continue
      const r = plane.region
      if (r.shape === 'circle') {
        const dx = x - r.cx
        const dy = y - r.cy
        if (dx * dx + dy * dy <= r.radius * r.radius) return plane.layer
      } else if (x >= r.minX && x <= r.maxX && y >= r.minY && y <= r.maxY) {
        return plane.layer
      }
    }
    return this.homePlane
  }

  /** A ball held by a kicker is not simulated at all — see `Kicker`. */
  private simulated(ball: Ball): boolean {
    return ball.active && ball.heldBy < 0
  }

  /** The machine frame: a stiff, heavily damped spring back to its resting position. */
  private stepFrame(dt: number): void {
    const ax = -FRAME_STIFFNESS * this.frameX - FRAME_DAMPING * this.frameVX
    const ay = -FRAME_STIFFNESS * this.frameY - FRAME_DAMPING * this.frameVY
    this.frameVX += ax * dt
    this.frameVY += ay * dt
    this.frameX += this.frameVX * dt
    this.frameY += this.frameVY * dt

    const d2 = this.frameX * this.frameX + this.frameY * this.frameY
    if (d2 > FRAME_MAX_DISPLACEMENT * FRAME_MAX_DISPLACEMENT) {
      const scale = FRAME_MAX_DISPLACEMENT / Math.sqrt(d2)
      this.frameX *= scale
      this.frameY *= scale
    }
  }

  private integrate(ball: Ball, dt: number): void {
    ball.touchingSurface = false

    if (this.gravityOn) {
      ball.vy -= A_DOWNFIELD * dt
      ball.vz -= A_NORMAL * dt
    }
    for (const d of this.forceDevices) if (d.enabled && (d.contactLayer < 0 || d.contactLayer === ball.layer)) d.applyForce(this, ball, dt)

    ball.clampSpeed(this.speedClamp)

    const x0 = ball.x
    const y0 = ball.y
    ball.x += ball.vx * dt
    ball.y += ball.vy * dt
    ball.z += ball.vz * dt

    this.sweptGuard(ball, x0, y0)
  }

  /**
   * Stop a fast ball at the first thing it would have passed through (M1-23, PH-A14).
   *
   * The ball forfeits whatever travel remains after that first touch. That is a real error,
   * bounded at 8 mm and confined to a step which would otherwise have lost the ball
   * entirely — a trade nobody has ever regretted.
   */
  private sweptGuard(ball: Ball, x0: number, y0: number): void {
    const dx = ball.x - x0
    const dy = ball.y - y0
    const travel2 = dx * dx + dy * dy
    const threshold = SWEPT_TRAVEL_FRACTION * ball.radius
    if (travel2 < threshold * threshold) return

    const qx0 = x0 - this.frameX
    const qy0 = y0 - this.frameY
    const qx1 = ball.x - this.frameX
    const qy1 = ball.y - this.frameY

    // One broadphase query covering the whole swept segment, not just its endpoints.
    const travel = Math.sqrt(travel2)
    const n = this.broadphase.query(
      (qx0 + qx1) * 0.5,
      (qy0 + qy1) * 0.5,
      travel * 0.5 + ball.radius,
    )

    let earliest = NO_HIT
    for (let i = 0; i < n; i++) {
      const collider = this.colliders[this.broadphase.candidates[i]!]
      if (collider === undefined) continue
      if (collider.layer !== ball.layer || !this.colliderLive(collider)) continue
      const t = sweptColliderTOI(collider, qx0, qy0, qx1, qy1, ball.radius)
      if (t < earliest) earliest = t
    }
    if (earliest > 1) return

    this.sweptClamps++
    const inv = 1 / travel
    ball.x = x0 + dx * earliest + dx * inv * SWEPT_BIAS
    ball.y = y0 + dy * earliest + dy * inv * SWEPT_BIAS
  }

  /**
   * Hand a genuine impact to the presentation tap.
   *
   * Two gates, and they are different things. `resting` is the solver's own answer to "was this
   * a bounce or a ball sitting still", and a resting contact is not an impact at any speed.
   * `CONTACT_REPORT_SPEED` then drops the inaudible remainder, which is most of what a ball
   * rolling along a rail produces.
   */
  private reportContact(ball: Ball, surface: number, result: ContactResult): void {
    if (this.contactTap === undefined) return
    if (result.resting || result.impactSpeed < CONTACT_REPORT_SPEED) return
    this.contactTap(ball.id, surface, result.impactSpeed, this.stepIndex)
  }

  /** Is this collider currently solid? A dropped target and an open gate are not. */
  private colliderLive(collider: Collider): boolean {
    if (collider.device < 0) return true
    const d = this.devices[collider.device]
    if (d === undefined) return true
    return d.enabled && d.collidersActive
  }

  /** The surface below and the glass above. */
  private resolveSurfaces(ball: Ball): boolean {
    let hit = false

    const floorDepth = ball.floorZ + ball.radius - ball.z
    if (floorDepth > PENETRATION_SLOP) {
      correctPenetration(ball, 0, 0, 1, floorDepth)
      const wood = this.materials.wood
      const landing = applyContactImpulse(ball, 0, 0, 1, wood, this.frameVX, this.frameVY, 0, this.rng)
      this.reportContact(ball, wood.index, landing)
      hit = true
    }

    const ceiling = this.glassHeight - ball.radius
    const glassDepth = ball.z - ceiling
    if (glassDepth > PENETRATION_SLOP) {
      correctPenetration(ball, 0, 0, -1, glassDepth)
      const glass = this.materials.glass
      const struck = applyContactImpulse(ball, 0, 0, -1, glass, this.frameVX, this.frameVY, 0, this.rng)
      this.reportContact(ball, glass.index, struck)
      hit = true
    }

    return hit
  }

  /**
   * Static geometry.
   *
   * The ball is queried in *frame space* — its position minus the nudge displacement —
   * so the whole static world can stay put while the machine appears to move. Surface
   * velocity is the frame velocity, and the contact solver's existing friction and
   * restitution maths does the rest for free.
   */
  private resolveStatics(ball: Ball): boolean {
    const qx = ball.x - this.frameX
    const qy = ball.y - this.frameY
    const n = this.broadphase.query(qx, qy, ball.radius)
    let hit = false

    for (let i = 0; i < n; i++) {
      const collider = this.colliders[this.broadphase.candidates[i]!]
      if (collider === undefined) continue
      if (collider.layer !== ball.layer || !this.colliderLive(collider)) continue
      this.contact.hit = false
      if (!colliderContact(collider, qx, qy, ball.radius, this.contact)) continue

      const nx = this.contact.nx
      const ny = this.contact.ny
      correctPenetration(ball, nx, ny, 0, this.contact.depth)
      const material = this.materials[collider.material]
      const result = applyContactImpulse(
        ball,
        nx,
        ny,
        0,
        material,
        this.frameVX,
        this.frameVY,
        0,
        this.rng,
        collider.scatter,
      )
      hit = true
      this.reportContact(ball, material.index, result)

      if (collider.device >= 0) {
        const device = this.devices[collider.device]
        // Read the impact speed now: the shared result object is overwritten by the next
        // contact, and a device that fires a coil is very much the next contact's problem.
        if (device !== undefined) {
          device.onColliderHit(this, ball, nx, ny, result.impactSpeed)
        }
      }
    }
    return hit
  }

  /** Flipper bats. Moving colliders, so they take the reaction as well as give the impulse. */
  private resolveFlippers(ball: Ball): boolean {
    const qx = ball.x - this.frameX
    const qy = ball.y - this.frameY
    let hit = false

    for (const f of this.flippers) {
      if (f.contactLayer >= 0 && f.contactLayer !== ball.layer) continue
      this.contact.hit = false
      const touched = taperedCapsuleContact(
        f.pivotX,
        f.pivotY,
        f.tipX,
        f.tipY,
        f.radiusPivot,
        f.radiusTip,
        qx,
        qy,
        ball.radius,
        this.contact,
      )
      if (!touched) continue

      f.surfaceVelocityAt(this.contact.px, this.contact.py, this.surfaceVelocity)

      correctPenetration(ball, this.contact.nx, this.contact.ny, 0, this.contact.depth)
      const rubber = this.materials.flipperRubber
      // The bat is the one collider in the machine with an inertia of its own, so it is the
      // one collider that belongs in the impulse denominator. `applyBallReaction` below hands
      // it the recoil; this hands the solver the mass that recoil comes out of, and the two
      // have to be the same bat or the contact invents energy.
      this.reactingBody.invInertia = f.invInertia
      this.reactingBody.rx = this.contact.px - f.pivotX
      this.reactingBody.ry = this.contact.py - f.pivotY
      // **The one contact in the machine whose normal may leave the playfield plane** (G7-01).
      //
      // `flipperContactTilt` is zero in every shipped configuration and in every fixture, so this
      // is the same three numbers it has always been — `Math.cos(0)` is 1 and `Math.sin(0)` is 0,
      // exactly, in binary64, so the arithmetic is bit-identical rather than merely close. What it
      // buys is a rig that can answer PH-A15's question: a capsule in the plane can only impart
      // sidespin, and a live catch is rubber turning a ball's downward momentum into backspin.
      //
      // Tilted *up*, out of the playfield, which is the direction real rubber sitting below the
      // ball's equator would push. See `FLIPPER_CONTACT_TILT`.
      // **The core's own `sin`/`cos`, never the platform's.** `Math.sin` is not bit-identical
      // across V8, .NET and IL2CPP, and a leaderboard score is admitted because a server
      // re-simulation reached the same number — so the lint that refuses it here is load-bearing
      // even for a value that is zero in every shipped run.
      //
      // **Bounded here rather than by whoever sets the field.** A guard that lives in the caller is
      // a guard the next caller does not have — and this one is a public field on a simulation
      // whose whole claim is that two runtimes agree, so a value outside the model's range must not
      // be able to reach the solver at all. `clamp` is the core's own; `Math.min` would be fine and
      // is not what the rest of this file uses.
      const tilt = clamp(this.flipperContactTilt, 0, MAX_FLIPPER_CONTACT_TILT)
      const flat = tilt === 0 ? 1 : cos(tilt)
      const lift = tilt === 0 ? 0 : sin(tilt)
      const result = applyContactImpulse(
        ball,
        this.contact.nx * flat,
        this.contact.ny * flat,
        lift,
        rubber,
        this.surfaceVelocity.x + this.frameVX,
        this.surfaceVelocity.y + this.frameVY,
        0,
        this.rng,
        -1,
        this.reactingBody,
      )
      f.applyBallReaction(this.contact.px, this.contact.py, result.impulseX, result.impulseY)
      this.reportContact(ball, rubber.index, result)
      hit = true
    }
    return hit
  }

  /**
   * Ball against ball (M1-22, PH-032).
   *
   * A full 3-D sphere test rather than the planar one the specification allows, because the
   * height state already exists and a planar test would collide a ball on an airball with
   * one rolling underneath it. The layer check remains, so a ball on a ramp cannot strike a
   * ball on the playfield below even where the two pass directly over one another.
   *
   * Equal masses, so the impulse denominator is exactly `2/m`. Pairs are visited in
   * `(i, j)` index order with `i < j`, which is what keeps a six-ball pile reproducible.
   */
  private resolveBallPairs(): boolean {
    const balls = this.balls
    const count = balls.length
    if (count < 2) return false

    let hit = false
    const contactDistance = BALL_DIAMETER
    const metal = this.materials.metal

    for (let i = 0; i < count; i++) {
      const a = balls[i]!
      if (!this.simulated(a)) continue
      for (let j = i + 1; j < count; j++) {
        const b = balls[j]!
        if (!this.simulated(b)) continue
        if (a.layer !== b.layer) continue

        const dx = b.x - a.x
        const dy = b.y - a.y
        const dz = b.z - a.z
        const d2 = dx * dx + dy * dy + dz * dz
        if (d2 >= contactDistance * contactDistance || d2 < 1e-18) continue

        const dist = Math.sqrt(d2)
        const inv = 1 / dist
        const nx = dx * inv
        const ny = dy * inv
        const nz = dz * inv

        // Split the overlap evenly — equal masses have equal claim on the space.
        const half = (contactDistance - dist) * 0.5
        a.x -= nx * half
        a.y -= ny * half
        a.z -= nz * half
        b.x += nx * half
        b.y += ny * half
        b.z += nz * half
        hit = true

        const vn = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny + (b.vz - a.vz) * nz
        if (vn >= 0) continue

        const approach = -vn
        // The lower index carries the report. One collision is one sound, and a pair that
        // reported twice would play the two-tone click at double level during every multiball
        // pile-up — which is precisely when it matters most that it is legible.
        if (
          this.contactTap !== undefined &&
          approach >= MIN_SEPARATION_VELOCITY &&
          approach >= CONTACT_REPORT_SPEED
        ) {
          this.contactTap(a.id, SURFACE_BALL, approach, this.stepIndex)
        }
        const e = approach < MIN_SEPARATION_VELOCITY ? 0 : effectiveRestitution(metal, approach)
        const jn = (-(1 + e) * vn) / (a.invMass + b.invMass)

        a.vx -= jn * nx * a.invMass
        a.vy -= jn * ny * a.invMass
        a.vz -= jn * nz * a.invMass
        b.vx += jn * nx * b.invMass
        b.vy += jn * ny * b.invMass
        b.vz += jn * nz * b.invMass
      }
    }
    return hit
  }
}
